import { fetchEventSource } from '@microsoft/fetch-event-source';

/**
 * Server-Sent-Events client for live order updates. Direct port of
 * `/portal/src/app/services/sseClient.js`.
 * Connects to `${baseUrl}/v1/realTime/userUpdates` with the bearer token
 * fetched lazily via the supplied callback (so the saga can refresh tokens
 * on demand without re-instantiating the client). Reconnects on transport
 * errors with capped exponential backoff; bails permanently on auth errors.
 * This class only speaks the transport — cross-tab leader election (one
 * connection per browser, frames mirrored to follower tabs) lives in
 * `tabCoordinator.ts` and is orchestrated by the middleware.
 * One intentional difference vs the legacy client: logger calls are wrapped
 * in `verbose &&` instead of a separate flag, keeping the verbose-logging
 * knob on the slice next to its config.
 */

const RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000] as const;
const MAX_RETRIES = RETRY_DELAYS_MS.length;
/** How long a dial may wait for response HEADERS before it is treated as a
 * dead connection and handed to the retry path. Only covers the handshake —
 * once headers arrive the stream may stay open indefinitely. */
const OPEN_TIMEOUT_MS = 15_000;
/** Treat an open stream as dead when NO frame (event or server heartbeat)
 * arrives for this long. The server emits a `: ping` comment every 30s of
 * idle, so this is ~3 missed heartbeats — the signature of a socket an
 * intermediary kept open after the upstream died (e.g. a dev proxy that
 * swallowed an upstream ECONNRESET). Neither onerror nor onclose fires in
 * that case; silence is the only observable signal. */
const SILENCE_TIMEOUT_MS = 90_000;
const LOG_PREFIX = '[SSE]';

export type SseMessage = {
  EventId?: string;
  EventType?: string;
  MerchantId?: string;
  SortKey?: string;
  Payload?: Record<string, unknown>;
  [key: string]: unknown;
};

export type SseOnMessage = (event: SseMessage) => void;
export type SseOnError = (err: Error) => void;
export type SseTokenProvider = () => Promise<string | null>;

/**
 * Lifecycle notifications the UI can render (connection chip). `open` fires on
 * every successful stream open; `retryScheduled` fires each time a backoff
 * timer is armed, carrying the attempt ordinal so the UI can show progress.
 * Terminal failures (auth, retries exhausted) still arrive via `onError`.
 */
export type SseStatusEvent =
  | { type: 'open' }
  | { type: 'retryScheduled'; attempt: number; maxRetries: number; delayMs: number };
export type SseOnStatus = (status: SseStatusEvent) => void;

export interface SseClientOptions {
  /** Base URL of the SSE microservice, e.g. `/sse`. */
  baseUrl: string;
  /** Lazy token accessor. Return null to abort the open. */
  getToken: SseTokenProvider;
  /** Emits one log line per significant lifecycle event when true. */
  verbose?: boolean;
}

export class SseClient {
  private baseUrl: string;
  private getToken: SseTokenProvider;
  private verbose: boolean;
  private controller: AbortController | null = null;
  private retryCount = 0;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private silenceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  /** True once the server has sent a comment frame (`: connected` / `: ping`).
 * The open-timeout and silence watchdog assume those frames exist, so they
 * stay DISARMED until this capability is observed — against a server that
 * doesn't send them (older deploy), the client behaves exactly like the
 * pre-hardening version instead of killing healthy quiet streams. Sticky
 * for the client's lifetime: a fixed server proves itself on every
 * connection open (`: connected`), so re-dials keep full hardening. */
  private serverSendsCommentFrames = false;
  /** Last callbacks passed to connect() — lets reconnectNow() re-dial with the
 * same handlers without the caller having to hold on to them. */
  private handlers: { onMessage: SseOnMessage; onError: SseOnError; onStatus?: SseOnStatus } | null =
    null;
  /** Preserved across reconnects so the server can resume at the correct
 * cursor. Cleared only when the client instance is dropped. */
  lastEventId: string | null = null;

  constructor({ baseUrl, getToken, verbose = false }: SseClientOptions) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
    this.verbose = verbose;
  }

  private buildEventLogContext(event: SseMessage): Record<string, unknown> {
    const base = { eventId: event.EventId, eventType: event.EventType };
    const payload = event.Payload as Record<string, unknown> | undefined;
    switch (event.EventType) {
      case 'orderUpdate':
        return {
          ...base,
          orderId: payload?.OrderId,
          oldStatus: payload?.OldStatus,
          newStatus: payload?.NewStatus,
          orderUpdatedAt: payload?.OrderUpdatedAt,
        };
      default:
        return base;
    }
  }

  private log(level: 'info' | 'warn' | 'error' | 'debug', message: string, context?: unknown): void {
    if (!this.verbose && level === 'debug') return;
    if (level === 'error') {
      console.error(`${LOG_PREFIX} ${message}`, context ?? '');
    } else if (level === 'warn') {
      console.warn(`${LOG_PREFIX} ${message}`, context ?? '');
    } else if (this.verbose) {
      // eslint-disable-next-line no-console
      console.log(`${LOG_PREFIX} ${message}`, context ?? '');
    }
  }

  connect(onMessage: SseOnMessage, onError: SseOnError, onStatus?: SseOnStatus): void {
    // Remember the handlers even when the guard below short-circuits, so a
    // later reconnectNow() always re-dials with the freshest callbacks.
    this.handlers = { onMessage, onError, onStatus };
    if (this.isConnecting) {
      this.log('debug', 'connect skipped — already connecting');
      return;
    }
    this.disconnect();
    this.isConnecting = true;

    this.controller = new AbortController();
    const signal = this.controller.signal;
    const url = `${this.baseUrl}/v1/realTime/userUpdates`;
    this.log('info', 'initiating connection', { url, lastEventId: this.lastEventId });

    const connectWithRetry = (): void => {
      if (signal.aborted) {
        this.isConnecting = false;
        return;
      }

      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      if (this.lastEventId) headers['Last-Event-ID'] = this.lastEventId;

      fetchEventSource(url, {
        signal,
        method: 'GET',
        headers,
        openWhenHidden: true,

        onopen: async (response) => {
          if (response.ok) {
            this.retryCount = 0;
            this.isConnecting = false;
            this.log('info', 'connected', { status: response.status });
            this.resetSilenceWatchdog();
            onStatus?.({ type: 'open' });
            return;
          }
          if (response.status === 401 || response.status === 403) {
            this.log('error', 'auth failure', { status: response.status });
            throw new Error(`Auth Error: ${response.status}`);
          }
          this.log('error', 'server error on open', { status: response.status });
          throw new Error(`Server Error: ${response.status}`);
        },

        // A stream that should live indefinitely ended cleanly — the server
        // (or an intermediary) closed it. Surface it as an error so the
        // backoff/retry path takes over instead of the UI silently showing a
        // live connection over a dead stream. The transport catches this
        // throw and routes it into onerror below. A deliberate disconnect()
        // aborts the signal instead, which never reaches onclose.
        onclose: () => {
          this.log('warn', 'stream closed by server — treating as an error');
          throw new Error('Stream closed by server');
        },

        onmessage: (msg) => {
          if (!msg.data) {
            // Comment frames (`: connected`, `: ping`) surface here as
            // empty-data messages — the parser dispatches on the blank line.
            // Their presence proves the server supports heartbeats, which is
            // what arms the silence watchdog and the open-timeout.
            this.serverSendsCommentFrames = true;
            this.log('debug', 'heartbeat frame received');
            this.resetSilenceWatchdog();
            if (msg.id) this.lastEventId = msg.id;
            return;
          }
          // Any frame — event or heartbeat — proves the stream is alive.
          this.resetSilenceWatchdog();
          if (msg.id) this.lastEventId = msg.id;
          try {
            const parsed = JSON.parse(msg.data) as SseMessage;
            if (parsed.EventId) this.lastEventId = parsed.EventId;
            this.log('debug', 'event received', this.buildEventLogContext(parsed));
            onMessage(parsed);
          } catch (e) {
            this.log('error', 'failed to parse event data', {
              error: (e as Error).message,
              raw: msg.data,
            });
          }
        },

        fetch: async (input, init) => {
          const token = await this.getToken();
          if (!token) {
            this.log('warn', 'no auth token — aborting fetch');
            throw new Error('Auth Error: No token available');
          }
          const next = init ?? {};
          next.headers = next.headers ?? {};
          (next.headers as Record<string, string>).Authorization = `Bearer ${token}`;
          return this.fetchWithOpenTimeout(input, next);
        },

        onerror: (err: Error) => {
          this.log('error', 'connection error', {
            error: err?.message,
            retryCount: this.retryCount,
          });

          if (err.message?.includes('Auth Error')) {
            this.log('warn', 'auth error — stopping reconnect attempts');
            onError(err);
            this.isConnecting = false;
            throw err;
          }

          if (this.retryCount >= MAX_RETRIES) {
            this.log('warn', `max retries (${MAX_RETRIES}) reached`);
            onError(new Error(`Connection failed after ${MAX_RETRIES} retries: ${err?.message || 'unknown'}`));
            this.isConnecting = false;
            throw err;
          }

          if (!signal.aborted) {
            if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
            const delay = RETRY_DELAYS_MS[Math.min(this.retryCount, RETRY_DELAYS_MS.length - 1)]!;
            this.retryCount++;
            this.log('info', 'scheduling reconnect', { attempt: this.retryCount, delayMs: delay });
            onStatus?.({
              type: 'retryScheduled',
              attempt: this.retryCount,
              maxRetries: MAX_RETRIES,
              delayMs: delay,
            });
            this.retryTimeoutId = setTimeout(() => {
              this.retryTimeoutId = null;
              connectWithRetry();
            }, delay);
          }

          throw err;
        },
      }).catch((err: unknown) => {
        this.log('debug', 'fetchEventSource promise rejected (handled)', {
          error: (err as Error)?.message,
        });
      });
    };

    connectWithRetry();
  }

  /**
   * (Re)arm the dead-stream watchdog. Called on open and on every received
   * frame; if it ever fires, the stream went silent past the server's
   * heartbeat cadence — a dead socket an intermediary kept open, invisible to
   * onerror/onclose — so drop the connection and re-dial (Last-Event-ID
   * replays anything missed).
   */
  private resetSilenceWatchdog(): void {
    // Silence is only meaningful when the server heartbeats — otherwise a
    // quiet-but-healthy stream is indistinguishable from a dead one and the
    // watchdog would kill it. Stay disarmed until the capability is proven.
    if (!this.serverSendsCommentFrames) return;
    if (this.silenceTimeoutId) clearTimeout(this.silenceTimeoutId);
    this.silenceTimeoutId = setTimeout(() => {
      this.silenceTimeoutId = null;
      this.log('warn', `no frames for ${SILENCE_TIMEOUT_MS}ms — treating stream as dead, re-dialling`);
      this.reconnectNow();
    }, SILENCE_TIMEOUT_MS);
  }

  private clearSilenceWatchdog(): void {
    if (this.silenceTimeoutId) {
      clearTimeout(this.silenceTimeoutId);
      this.silenceTimeoutId = null;
    }
  }

  /**
   * fetch() that rejects when the response HEADERS don't arrive within
   * `OPEN_TIMEOUT_MS`. Without this, a dial that hangs — a gateway that
   * accepts the socket but never flushes headers, or a blackholed
   * cross-origin request — pends forever and the UI sticks at "Connecting…"
   * with no error ever surfacing. The rejection lands in the transport's
   * onerror, i.e. the normal backoff/retry path.
   */
  private async fetchWithOpenTimeout(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
    // A server that never sends comment frames also doesn't flush headers
    // until its first event, so a slow handshake there is normal, not a dead
    // dial — timing it out would kill healthy quiet streams. Until the
    // heartbeat capability is observed, dial without the guard (the
    // pre-hardening behavior).
    if (!this.serverSendsCommentFrames) {
      return fetch(input, init);
    }
    // One controller governs both the handshake timeout and — after headers
    // arrive — the stream teardown, relayed from the transport's own signal.
    const upstream = init.signal;
    const controller = new AbortController();
    if (upstream?.aborted) {
      controller.abort();
    } else {
      upstream?.addEventListener('abort', () => controller.abort(), { once: true });
    }
    const timeoutId = setTimeout(() => {
      this.log('warn', `no response headers after ${OPEN_TIMEOUT_MS}ms — aborting dial`);
      controller.abort();
    }, OPEN_TIMEOUT_MS);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } finally {
      // Headers arrived (or the dial failed) — the timeout only guards the
      // handshake, never the long-lived stream that follows.
      clearTimeout(timeoutId);
    }
  }

  /**
   * Re-dial immediately, skipping any pending exponential-backoff wait.
   * disconnect() clears the armed retry timer, aborts the current stream, and
   * resets the retry counter, so the fresh connect() starts the backoff
   * schedule from the top. No-op until connect() has been called once (there
   * are no handlers to re-dial with). Safe against rapid repeat calls: the
   * fresh connect() sets `isConnecting`, so a second reconnectNow() while the
   * dial is in flight lands on the connect() guard.
   */
  reconnectNow(): void {
    if (!this.handlers) {
      this.log('debug', 'reconnectNow skipped — never connected');
      return;
    }
    const { onMessage, onError, onStatus } = this.handlers;
    this.log('info', 'immediate reconnect requested');
    this.disconnect();
    this.connect(onMessage, onError, onStatus);
  }

  disconnect(): void {
    this.log('info', 'disconnecting', { wasConnecting: this.isConnecting });
    this.isConnecting = false;
    this.clearSilenceWatchdog();
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    this.retryCount = 0;
  }

  setVerbose(verbose: boolean): void {
    this.verbose = verbose;
  }
}
